import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { MotionPlugin } from '@vueuse/motion';
import * as Sentry from '@sentry/vue';
import App from './App.vue';
import router from './router';
import getConfig from './utils/config';
import {
  initMsalRouter,
  $msal,
  setupMsalEventCallbacks,
  handleRedirectResponse
} from './plugins/msal';
import { setSentryUserContext } from './utils/sentry-context';

// Add global error handlers before MSAL initialization
window.addEventListener('unhandledrejection', event => {
  Sentry.logger.error('Unhandled promise rejection', {
    error: event.reason,
    log_source: 'global_error_handler'
  });
});

window.addEventListener('error', event => {
  Sentry.logger.error('Global error', { error: event.error, log_source: 'global_error_handler' });
});

// Asynchronously initialize MSAL before mounting the app
$msal
  .initialize()
  .then(async () => {
    try {
      // Setup MSAL event callbacks after initialization
      setupMsalEventCallbacks();

      // Handle any pending redirect response immediately
      await handleRedirectResponse();

      const pinia = createPinia();
      const app = createApp(App);

      initMsalRouter(router);

      // Add Vue error handler before Sentry init
      app.config.errorHandler = (error, instance, info) => {
        Sentry.logger.error('Vue error', {
          error: String(error),
          info,
          log_source: 'vue_error_handler'
        });
      };

      Sentry.init({
        app,
        // dsn: getConfig('VITE_SENTRY_DSN'),
        dsn: 'https://8254856d9be017c378e20bfdc1b4e0f9@o4510347413225472.ingest.us.sentry.io/4510347494621184',
        enableLogs: true,
        integrations: [
          Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
          Sentry.browserTracingIntegration({ router }),
          Sentry.replayIntegration(),
          Sentry.browserProfilingIntegration(),
          Sentry.vueIntegration({
            tracingOptions: {
              trackComponents: true
            }
          })
        ],
        tracesSampleRate: 1.0,
        tracePropagationTargets: ['localhost', getConfig('VITE_APP_API_BASE_URL')],
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 1.0,
        beforeSend(event, hint) {
          // Log errors before sending to Sentry
          Sentry.logger.info('Sentry event being sent', {
            event,
            hint,
            log_source: 'sentry_before_send'
          });
          return event;
        }
      });

      // Set user context if user is already logged in
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr) as SecurityUser;
          setSentryUserContext(user);
          Sentry.logger.info('User context initialized', {
            log_source: 'app_init',
            user_id: user.id,
            organization_id: user.organizationId
          });
        }
      } catch (err) {
        Sentry.logger.warn('Failed to set initial Sentry user context', {
          log_source: 'app_init',
          error: String(err)
        });
      }

      app.use(router).use(pinia).use(MotionPlugin);

      // Wait for router to be ready before mounting
      await router.isReady();
      app.mount('#app');

      Sentry.logger.info('Application mounted successfully', {
        log_source: 'app_init'
      });
    } catch (error) {
      Sentry.logger.error('Application initialization failed', {
        log_source: 'app_init',
        error: String(error)
      });
      // Optionally show a user-friendly error message
    }
  })
  .catch(error => {
    Sentry.logger.error('MSAL initialization failed', {
      log_source: 'msal_init',
      error: String(error)
    });
    // Optionally show a user-friendly error message or fallback
  });
